import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Plug, Plus, RefreshCw, Settings, Trash2, Copy, ExternalLink,
  CheckCircle, AlertCircle, Code, Webhook, Key, FileJson, Book
} from 'lucide-react';

const API = `${window.location.origin}/api`;

const EInvoiceIntegrations = () => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);
  
  const [newMapping, setNewMapping] = useState({
    source: '',
    mappings: {}
  });
  const [mappingFields, setMappingFields] = useState([
    { source: '', target: '' }
  ]);

  // Predefined integrations
  const predefinedIntegrations = [
    { 
      name: 'Beds24', 
      logo: '/logos/beds24.png',
      status: 'available',
      description: 'Popular hotel channel manager'
    },
    { 
      name: 'Cloudbeds', 
      logo: '/logos/cloudbeds.png',
      status: 'available',
      description: 'Lightweight booking & record management'
    },
    { 
      name: 'SiteMinder', 
      logo: '/logos/siteminder.png',
      status: 'available',
      description: 'Channel manager & booking engine'
    },
    { 
      name: 'HotelRunner', 
      logo: '/logos/hotelrunner.png',
      status: 'available',
      description: 'Distribution & direct booking'
    },
    { 
      name: 'Little Hotelier', 
      logo: null,
      status: 'available',
      description: 'Small property management'
    },
    { 
      name: 'Custom PMS', 
      logo: null,
      status: 'available',
      description: 'Custom API integration'
    }
  ];

  // Standard target fields
  const standardFields = [
    'booking_id', 'guest_name', 'guest_email', 'check_in', 'check_out',
    'room_name', 'amount', 'tax', 'currency', 'status'
  ];

  useEffect(() => {
    fetchMappings();
  }, []);

  const fetchMappings = async () => {
    try {
      const res = await axios.get(`${API}/einvoice/integrations/mappings`);
      setMappings(res.data.mappings || []);
    } catch (error) {
      console.error('Error fetching mappings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMappingField = () => {
    setMappingFields([...mappingFields, { source: '', target: '' }]);
  };

  const handleRemoveMappingField = (index) => {
    setMappingFields(mappingFields.filter((_, i) => i !== index));
  };

  const handleMappingFieldChange = (index, field, value) => {
    const updated = [...mappingFields];
    updated[index][field] = value;
    setMappingFields(updated);
  };

  const handleSaveMapping = async () => {
    if (!newMapping.source) {
      toast.error('Please enter a source name');
      return;
    }

    const validFields = mappingFields.filter(f => f.source && f.target);
    if (validFields.length === 0) {
      toast.error('Please add at least one field mapping');
      return;
    }

    const mappingsObj = {};
    validFields.forEach(f => {
      mappingsObj[f.source] = f.target;
    });

    try {
      await axios.post(`${API}/einvoice/integrations/mappings`, {
        source: newMapping.source,
        mappings: mappingsObj
      });
      toast.success('Field mapping saved');
      setShowMappingModal(false);
      resetMappingForm();
      fetchMappings();
    } catch (error) {
      toast.error('Failed to save mapping');
    }
  };

  const handleDeleteMapping = async (source) => {
    if (!confirm(`Delete mapping for ${source}?`)) return;
    
    try {
      await axios.delete(`${API}/einvoice/integrations/mappings/${source}`);
      toast.success('Mapping deleted');
      fetchMappings();
    } catch (error) {
      toast.error('Failed to delete mapping');
    }
  };

  const resetMappingForm = () => {
    setNewMapping({ source: '', mappings: {} });
    setMappingFields([{ source: '', target: '' }]);
    setSelectedMapping(null);
  };

  const openEditMapping = (mapping) => {
    setSelectedMapping(mapping);
    setNewMapping({ source: mapping.source, mappings: mapping.mappings });
    
    const fields = Object.entries(mapping.mappings || {}).map(([source, target]) => ({
      source, target
    }));
    if (fields.length === 0) fields.push({ source: '', target: '' });
    setMappingFields(fields);
    setShowMappingModal(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const webhookUrl = `${window.location.origin}/api/einvoice/webhooks/bookings`;
  const apiEndpoint = `${window.location.origin}/api/einvoice/bookings/import`;

  return (
    <div className="space-y-6" data-testid="einvoice-integrations">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Integrations</h1>
          <p className="text-slate-600">Connect your PMS and channel managers</p>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="mappings">Field Mappings</TabsTrigger>
          <TabsTrigger value="api">API Documentation</TabsTrigger>
          <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {predefinedIntegrations.map((integration) => {
              const hasMapping = mappings.some(m => m.source === integration.name);
              return (
                <Card key={integration.name} className={hasMapping ? 'border-green-200 bg-green-50/30' : ''}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-zinc-800 border border-slate-200 dark:border-zinc-700 flex items-center justify-center overflow-hidden">
                          {integration.logo ? (
                            <img src={integration.logo} alt={integration.name} className="w-7 h-7 object-contain" />
                          ) : (
                            <Plug className="w-5 h-5 text-slate-400" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold">{integration.name}</h3>
                          <p className="text-xs text-slate-500">{integration.description}</p>
                        </div>
                      </div>
                      {hasMapping && (
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Configured
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          const existing = mappings.find(m => m.source === integration.name);
                          if (existing) {
                            openEditMapping(existing);
                          } else {
                            resetMappingForm();
                            setNewMapping({ source: integration.name, mappings: {} });
                            setShowMappingModal(true);
                          }
                        }}
                      >
                        <Settings className="w-4 h-4 mr-1" />
                        {hasMapping ? 'Edit' : 'Configure'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Field Mappings Tab */}
        <TabsContent value="mappings">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Field Mappings</CardTitle>
                <CardDescription>Map source fields to standard e-Invoice fields</CardDescription>
              </div>
              <Button onClick={() => {
                resetMappingForm();
                setShowMappingModal(true);
              }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Mapping
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : mappings.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileJson className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No field mappings configured</p>
                  <p className="text-sm">Add mappings to transform data from your PMS</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Source</TableHead>
                      <TableHead>Fields Mapped</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mappings.map((mapping) => (
                      <TableRow key={mapping.id || mapping.source}>
                        <TableCell className="font-medium">{mapping.source}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {Object.keys(mapping.mappings || {}).length} fields
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-500">
                          {mapping.created_at ? new Date(mapping.created_at).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEditMapping(mapping)}>
                              <Settings className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteMapping(mapping.source)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* API Documentation Tab */}
        <TabsContent value="api">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Book className="w-5 h-5" />
                Booking Import API
              </CardTitle>
              <CardDescription>Use this API to import bookings from your PMS</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>Endpoint</Label>
                <div className="flex items-center gap-2 mt-2">
                  <code className="flex-1 bg-slate-100 p-3 rounded font-mono text-sm">
                    POST {apiEndpoint}
                  </code>
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(apiEndpoint)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div>
                <Label>Example Payload</Label>
                <pre className="bg-slate-900 text-green-400 p-4 rounded mt-2 overflow-x-auto text-sm">
{`{
  "booking_id": "B12345",
  "source": "Beds24",
  "property_id": "your-property-id",
  "guest_name": "John Tan",
  "guest_email": "john@email.com",
  "check_in": "2026-03-10",
  "check_out": "2026-03-12",
  "room_name": "Deluxe Room",
  "amount": 320,
  "tax": 0,
  "currency": "MYR"
}`}
                </pre>
              </div>

              <div>
                <Label>Response</Label>
                <pre className="bg-slate-100 p-4 rounded mt-2 overflow-x-auto text-sm">
{`{
  "id": "uuid-generated",
  "booking_id": "B12345",
  "source": "Beds24",
  "status": "confirmed",
  "created_at": "2026-03-13T12:00:00Z"
}`}
                </pre>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">Authentication</h4>
                <p className="text-sm text-blue-700">
                  Include your API key in the Authorization header:
                </p>
                <code className="block mt-2 bg-blue-100 p-2 rounded text-sm">
                  Authorization: Bearer YOUR_API_KEY
                </code>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Webhooks Tab */}
        <TabsContent value="webhooks">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Webhook className="w-5 h-5" />
                Webhook Endpoint
              </CardTitle>
              <CardDescription>Configure your PMS to send booking events to this URL</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>Webhook URL</Label>
                <div className="flex items-center gap-2 mt-2">
                  <code className="flex-1 bg-slate-100 p-3 rounded font-mono text-sm break-all">
                    {webhookUrl}
                  </code>
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(webhookUrl)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div>
                <Label>Supported Events</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {['booking.created', 'booking.modified', 'booking.cancelled', 'booking.checkedout'].map(event => (
                    <div key={event} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <code className="text-sm">{event}</code>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label>Webhook Payload Format</Label>
                <pre className="bg-slate-900 text-green-400 p-4 rounded mt-2 overflow-x-auto text-sm">
{`{
  "event": "booking.checkedout",
  "booking_id": "B12345",
  "source": "Beds24",
  "property_id": "your-property-id",
  "data": {
    "guest_name": "John Tan",
    "guest_email": "john@email.com",
    "check_in": "2026-03-10",
    "check_out": "2026-03-12",
    "amount": 320
  }
}`}
                </pre>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-semibold text-amber-800 mb-2">Auto-Invoice on Checkout</h4>
                <p className="text-sm text-amber-700">
                  When a <code>booking.checkedout</code> event is received, an e-Invoice is automatically 
                  generated and queued for LHDN submission.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Field Mapping Modal */}
      <Dialog open={showMappingModal} onOpenChange={setShowMappingModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedMapping ? 'Edit' : 'Add'} Field Mapping
            </DialogTitle>
            <DialogDescription>
              Map fields from your PMS to standard e-Invoice fields
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="source">Source Name *</Label>
              <Input
                id="source"
                value={newMapping.source}
                onChange={(e) => setNewMapping({...newMapping, source: e.target.value})}
                placeholder="e.g., Beds24, Cloudbeds"
                disabled={!!selectedMapping}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Field Mappings</Label>
                <Button type="button" variant="outline" size="sm" onClick={handleAddMappingField}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Field
                </Button>
              </div>
              
              <div className="space-y-2">
                {mappingFields.map((field, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={field.source}
                      onChange={(e) => handleMappingFieldChange(index, 'source', e.target.value)}
                      placeholder="Source field (e.g., guestName)"
                      className="flex-1"
                    />
                    <span className="text-slate-400">→</span>
                    <select
                      value={field.target}
                      onChange={(e) => handleMappingFieldChange(index, 'target', e.target.value)}
                      className="flex-1 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="">Select target field</option>
                      {standardFields.map(f => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                    {mappingFields.length > 1 && (
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleRemoveMappingField(index)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMappingModal(false)}>Cancel</Button>
            <Button onClick={handleSaveMapping}>
              Save Mapping
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EInvoiceIntegrations;
